import { useFormatAmount, useIntl } from '@capsicum/common';
import { useState } from 'react';

import { type ConfirmBidProps } from '../../models';

export const useConfirmBid = ({
	closeModal,
	country,
	onBidSubmit
}: {
	closeModal: () => void;
	country: string;
	onBidSubmit: () => Promise<unknown>;
}) => {
	const { $t } = useIntl();
	const [consent, setConsent] = useState<boolean>(false);
	const [confirmBidDisabled, setConfirmBidDisabled] = useState<boolean>(false);
	const { formatAmount } = useFormatAmount();

	const texts = {
		bid: $t('rewards.auctions.bids.confirmDrawer.bid'),
		buttonText: $t('rewards.auctions.bids.confirmDrawer.button'),
		buyerPremiumTitle: (percentage: number) =>
			$t('rewards.auctions.bids.confirmDrawer.buyerPremiumTitle', {
				percentage
			}),
		buyerPremiumTooltipText: (percentage: number) =>
			$t('rewards.auctions.bids.confirmDrawer.buyerPremiumTooltipText', {
				percentage
			}),
		buyerPremiumTooltipTitle: $t('rewards.auctions.bids.confirmDrawer.buyerPremiumTooltipTitle'),
		deliveryTo: $t('rewards.auctions.bids.confirmDrawer.deliveryTo', { country }),
		description: $t('rewards.auctions.bids.confirmDrawer.description'),
		shipping: $t('rewards.auctions.bids.confirmDrawer.shipping'),
		title: $t('rewards.auctions.bids.confirmDrawer.title'),
		total: $t('rewards.auctions.bids.confirmDrawer.total'),
		vat: $t('rewards.auctions.bids.confirmDrawer.vat'),
		vatTooltipText: (vat: string) =>
			$t('rewards.auctions.bids.confirmDrawer.vatTooltipText', {
				vat
			}),
		vatTooltipTitle: $t('rewards.auctions.bids.confirmDrawer.vat'),
		warning: $t('rewards.auctions.bids.confirmDrawer.warning')
	};

	const getTableItems = ({
		bidAmountChz,
		bidAmountFiat,
		buyersPremiumChz,
		buyersPremiumFiat,
		buyersPremiumPercentage,
		chzCurrencyShortcode,
		estimatedShippingCostChz,
		estimatedShippingCostFiat,
		fiatCurrencyShortcode,
		totalChz,
		totalFiat,
		vatChz,
		vatFiat
	}: Omit<ConfirmBidProps, 'closeModal' | 'country' | 'onBidSubmit' | 'onClose' | 'testID'>) => {
		const bidRow = {
			price: `${formatAmount(bidAmountFiat, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${fiatCurrencyShortcode}`,
			priceChz: `(${formatAmount(bidAmountChz, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${chzCurrencyShortcode})`,
			title: `${texts.bid}:`
		};
		const BuyerPremium = {
			price: `${formatAmount(buyersPremiumFiat, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${fiatCurrencyShortcode}`,
			priceChz: `(${formatAmount(buyersPremiumChz, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${chzCurrencyShortcode})`,
			title: texts.buyerPremiumTitle(buyersPremiumPercentage),
			toolTipText: texts.buyerPremiumTooltipText(buyersPremiumPercentage),
			toolTipTitle: texts.buyerPremiumTooltipTitle
		};
		const vat = {
			price: `${formatAmount(vatFiat, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${fiatCurrencyShortcode}`,
			priceChz: `(${formatAmount(vatChz, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${chzCurrencyShortcode})`,
			title: texts.vat,
			toolTipText: texts.vatTooltipText(`${vatFiat} ${fiatCurrencyShortcode}`),
			toolTipTitle: texts.vat
		};
		const shippingRow = {
			price: `${formatAmount(estimatedShippingCostFiat, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${fiatCurrencyShortcode}`,
			priceChz: `(${formatAmount(estimatedShippingCostChz, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${chzCurrencyShortcode})`,
			title: `${texts.shipping}:`
		};
		const totalRow = {
			isBold: true,
			price: `${formatAmount(totalFiat, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${fiatCurrencyShortcode}`,
			priceChz: `(${formatAmount(totalChz, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2
			})} ${chzCurrencyShortcode})`,
			title: `${texts.total}:`
		};

		return [bidRow, shippingRow, BuyerPremium, vat, totalRow];
	};

	const buttonPressHandler = () => {
		setConfirmBidDisabled(true);
		onBidSubmit().finally(() => setConfirmBidDisabled(false));
		closeModal();
	};

	return {
		buttonPressHandler,
		confirmBidDisabled,
		consent,
		getTableItems,
		setConfirmBidDisabled,
		setConsent,
		texts
	};
};
